/* eslint-disable react/no-children-prop */
import {
  CloseCircleOutlined,
  CloseOutlined,
  ExclamationCircleFilled,
  InfoCircleOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import { useMutation, useQuery } from "@apollo/client";
import {
  Badge,
  Button,
  Checkbox,
  Dropdown,
  Input,
  List,
  message,
  Modal,
  Popover,
  Radio,
  Tabs,
  Tag,
  Tooltip,
  Pagination,
} from "antd";
import dayjs from "dayjs";
import { isEmpty, isFunction, isUndefined, orderBy } from "lodash";
import React, { useContext, useEffect, useState, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { LuX } from "react-icons/lu";
import { PiUserCircle, PiUserCircleDashed } from "react-icons/pi";
import { UserContext } from "../../../amplify/authenticator-provider";
import { colorCode, PROJECT_NAME_V2 } from "../../../config";
import { UPDATE_LEAD_ARCHIVE_STATUS } from "../../../graphql/mutation";
import useLang from "../../../hooks/useLang";
import ArchiveIcon from "../../../icons/js/ArchiveIcon";
import LeadOppTypeIcons from "./LeadOppTypeIcons";
import FilterIcon from "../../../icons/js/FilterIcon";
import PinIcon from "../../../icons/js/PinIcon";
import RestoreArchiveIcon from "../../../icons/js/RestoreArchive";
import UnpinIcon from "../../../icons/js/UnpinIcon";
import SortIcon from "../../../icons/js/SortIcon";
import { parseTimestamp } from "../../../library/utils";
import {
  getDefaultTabId,
  getMostRecentPermittedRootOppTypeId,
  oppMatchesTab,
} from "../../../library/oppTypeScoping";
import { getCacheApi, setSelectedLeadPhone } from "../../../redux/slices/apiCacheSlice";
import { resetAiResponseHistoryIds } from "../../../redux/slices/aiSlice";
import {
  AccessPermissionAction,
  SEARCH_SCOPE,
  SEARCH_SCOPE_OPTIONS,
  isConversationMessage,
} from "../../../library/constants";
import { CustomRadioGroup } from "../../commonComponents/CustomRadio";
import CustomEmpty from "../../commonComponents/CustomEmpty";
import { checkAccessPermission } from "../../../library/helpers";
import {
  addQuickFilter,
  removeMyConversationFlag,
  removeQuickFilter,
  resetLeadFilters,
  setCameFromDashboard,
  setLeadSortOrder,
  setSearchRedux,
  setSearchAllLeads,
  changeMessageOpportunity,
  QUICK_FILTERS,
  getQuickFilterChipClass,
  getQuickFilterDotColor,
} from "../../../redux/slices/leadSlice";
import CustomAlert from "../../commonComponents/alert";
import CustomLabel from "../../commonComponents/label";
import { getOpportunityStatusPath, statusColor } from "./engagementHelpers";
import ActivityCenterFilter from "./ActivityCenterFilter";
import LeadList from "./leadList";
import DownIcon from "../../../icons/DownIcon";
import useBrowserExt from "../../../hooks/useBrowserExt";
import usePinnedLeads from "../../../hooks/usePinnedLeads";
import useLocationHelpers from "../../../hooks/useLocationHelpers";
import { GENERAL_CONFIG_KEYS, LOCATION_SETTING_KEYS } from "../../../constant/LocationConstant";
import { useWindowSize } from "../../../hooks/personalizedHooks";
import { GET_OPPORTUNITY_STATUS } from "../../../graphql/query";
import useDebounce from "../../../hooks/debounceHook";
import {
  getLocalStorage,
  setLocalStorage,
  SKIP_ARCHIVE_RESTORE_CONFIRMATION_KEY,
} from "../../../utils/storages";

const MIN_SEARCH_LENGTH = 3;
const SEARCH_DEBOUNCE_MS = 400;

// Lead-card left-strip color resolver. Priority per product decision:
//   1. NO_RESPONSE_NEEDED  — wins outright if present in ANY of the three
//                            fields (engagementStatus / replyState /
//                            messageStatusType).
//   2. NOT_RESPONDED       — when engagementStatus says so (overrides
//                            replyState).
//   3. replyState          — WAITING_FOR_REPLY / NEEDS_YOUR_REPLY.
//   4. engagementStatus    — remaining values (UNCONTACTED / RESPONDED /
//                            NOT_ENGAGED / ENGAGED).
//   5. Fallback            — UNCONTACTED.
const resolveLeadStripStatus = lead => {
  const engagementStatus = lead?.leadMessageSummary?.engagementStatus;
  const replyState = lead?.leadMessageSummary?.replyState;
  const messageStatus = lead?.messageStatusType?.status;

  if (
    engagementStatus === QUICK_FILTERS.NO_RESPONSE_NEEDED ||
    replyState === QUICK_FILTERS.NO_RESPONSE_NEEDED ||
    messageStatus === QUICK_FILTERS.NO_RESPONSE_NEEDED
  ) {
    return QUICK_FILTERS.NO_RESPONSE_NEEDED;
  }

  if (engagementStatus === QUICK_FILTERS.NOT_RESPONDED) return QUICK_FILTERS.NOT_RESPONDED;

  if (replyState === QUICK_FILTERS.WAITING_FOR_REPLY) return QUICK_FILTERS.WAITING_FOR_REPLY;
  if (replyState === QUICK_FILTERS.NEEDS_YOUR_REPLY) return QUICK_FILTERS.NEEDS_YOUR_REPLY;

  if (engagementStatus === QUICK_FILTERS.UNCONTACTED) return QUICK_FILTERS.UNCONTACTED;
  if (engagementStatus === QUICK_FILTERS.RESPONDED) return QUICK_FILTERS.RESPONDED;
  if (engagementStatus === QUICK_FILTERS.NOT_ENGAGED) return QUICK_FILTERS.NOT_ENGAGED;
  if (engagementStatus === QUICK_FILTERS.ENGAGED) return QUICK_FILTERS.ENGAGED;

  return QUICK_FILTERS.UNCONTACTED;
};

// Lead-list grouping tabs.
//
// "All" and "Pinned" are the SAME `getLeads` query — Pinned just adds
// `isPinned: true` — so both paginate, sort and filter server-side. The active
// tab lives in engagementActivity, which owns the query variables; it is
// exported because switching tab has to rewrite `isPinned` there.
// "Pinned from LC" is the odd one out: transient page state, filtered here.
export const LEAD_TABS = {
  ALL: "all",
  PINNED: "pinned",
  PINNED_FROM_LC: "pinnedFromLc",
};

const LeadListContainer = props => {
  const {
    setShowLeadSection,
    selectedLead,
    leadsLoading,
    leadList,
    setLeadList,
    variables,
    leadCount,
    unreadLeadsCount = null,
    onPinsChanged = () => {},
    setVariables,
    setSelectedLead,
    setMessageFilters,
    messageFilters,
    sourcesData,
    searchKey,
    handleSearch,
    setSearchKey,
    activeOpp,
    setActiveOpp,
    opportunityList,
    timeReference,
    setOpportunityList,
    setFilterApplied,
    handleOppFilter,
    handleStatusFilter,
    leadStatus,
    selectedStatus,
    setSelectedStatus,
    openAppointmentPopup,
    handleAppointmentModalClose,
    setSelectedLeadId,
    setbtnClicked,
    setActiveLeadId,
    handleScrollOnMovement,
    changePage,
    setQueryString = () => {},
    isFromChatbot = false,
    handleLeadListVisible,
    IsLeadListVisible,
    spinner,
    campaignFilters,
    queryString,
    entityId,
    eventId,
    handleLeadListDrawer = () => {},
    onClose,
    setAttachments,
    refetchLeads,
    setmessageText = () => {},
    setSummaryVisible,
    handleUnpinLead = () => {},
    selectedLeadsFromLeadCenter = [],
    isLeadCenterApply = false,
    getDuplicateLeads = () => {},
    getMergedLead = () => {},
    removeMergedLead = () => {},
    onClearAllFilters,
    isSingleLeadView = false,
    showLeadTabs: showLeadTabsProp = null,
    onLoadAllLeads = () => {},
    selectedOppTypeId = null,
    setSelectedOppTypeId = () => {},
    listFilterRootOppTypeIds = [],
  } = props;
  const { isBrowserExt } = useBrowserExt();
  const { width, height } = useWindowSize();
  const quickFilters = [
    {
      title: "Inbox",
      backgroundColor: "processing",
      value: "RESPONDED",
    },
    {
      title: "Sent",
      backgroundColor: "success",
      value: "WAITING_FOR_REPLY",
    },
    {
      title: "Archived",
      backgroundColor: "error",
      value: "NO_RESPONSE_NEEDED",
    },
  ];
  const urlParams = new URLSearchParams(window.location.search);
  const presetLeadFilter = urlParams.get("presetFilter");

  const { user, modal, newLead, setNewLead, addnewLead, setAddNewLead } = useContext(UserContext);
  const dispatch = useDispatch();
  const [lang] = useLang();
  const selectedLeadPhoneReduxData = useSelector(state => getCacheApi(state, "selectedLeadPhone"));
  const {
    quickFilters: selectedQuickFilters,
    userFilters,
    teamFilters = [],
    dateFilterType = "",
    dateRange: leadDateRange = [],
    opportunityTypeFilterCount,
    opportunityStatusFilterCount,
    opportunitySourceFilterCount,
    campaignFilters: reduxCampaignFilters,
  } = useSelector(state => state.lead.filters);
  const { leads, leadSort, search } = useSelector(state => state.lead);
  const [visible, setVisible] = useState(false);
  const [leadData, setLeadData] = useState([]);
  // Per-lead "Show All / Close" merge-banner state, keyed by stable lead id
  // (NOT list index — see banner). Absent id = collapsed → defaults to "Show All".
  const [showAll, setShowAll] = useState({});
  const [noResponseNeededLoading, setNoResponseNeededLoading] = useState(false);
  const [archivingLeadId, setArchivingLeadId] = useState(null);
  const [opportunityStatusList, setOpportunityStatusList] = useState([]);
  const listContainerRef = useRef(null);

  // Tabs are noise over a single lead, and in the webapp `isSingleLeadView`
  // (a ?leadId= deep link) is exactly that signal.
  //
  // browser-extension-v4 overloads the same prop with CRM-context state: its
  // initialiser ORs in `!isTekionCrm`, so the prop is true for every
  // non-Tekion dealership — including on a bare /browser-ext-v4/ with no lead
  // selected, where the list is a full list and the tabs do belong. It also
  // ORs in `!!search`. So a parent whose flag means something else can say
  // directly whether the tabs belong, rather than have it inferred.
  const showLeadTabs = typeof showLeadTabsProp === "boolean" ? showLeadTabsProp : !isSingleLeadView;

  // The All/Pinned split IS the query — `variables.isPinned` is the single
  // source of truth for it, so the tab is derived rather than stored.
  //
  // That matters because this component has five parents: engagementActivity
  // mounts it twice (sidebar + mobile drawer), and the four browser-extension
  // pages mount it once each. Parent-owned tab state desynced the two copies
  // in the first case and, in the second, silently froze the tabs on "All" for
  // every parent that didn't wire the handler — which was all of them.
  // Deriving it means a parent only has to keep passing `variables` and
  // `setVariables`, which all five already do.
  //
  // "Pinned from LC" is the exception: it changes nothing about the query, so
  // it stays local UI state.
  const [isLcTabActive, setIsLcTabActive] = useState(false);
  // eslint-disable-next-line no-nested-ternary
  const activeLeadTab = isLcTabActive
    ? LEAD_TABS.PINNED_FROM_LC
    : variables?.isPinned
    ? LEAD_TABS.PINNED
    : LEAD_TABS.ALL;

  const onLeadTabChange = key => {
    setIsLcTabActive(key === LEAD_TABS.PINNED_FROM_LC);
    // The LC tab renders page state, so it leaves the query alone.
    if (key === LEAD_TABS.PINNED_FROM_LC) return;
    // Never send `false`: the arg is tri-state on the server and false means
    // "unpinned only". Page resets — page 3 of All is rarely a valid page of a
    // much smaller pinned set.
    const nextIsPinned = key === LEAD_TABS.PINNED ? true : undefined;
    if ((variables?.isPinned ? true : undefined) === nextIsPinned) return;
    setVariables({ ...variables, page: 1, isPinned: nextIsPinned });
  };

  const isPinnedTab = showLeadTabs && activeLeadTab === LEAD_TABS.PINNED;
  // How many pinned leads are in scope. `getLeads` no longer returns a
  // standalone pin total, so this is only knowable while Pinned is the active
  // tab — there, the query IS the pin set and `leadCount` counts it. It is
  // narrowed by the active filters and search like any other tab count.
  const pinnedInScope = isPinnedTab ? leadCount : null;

  // Raised by an unpin, lowered by the first settled Pinned-tab result after
  // it. Distinguishes "the user emptied this tab" from "a filter hid its rows"
  // — a distinction the row count alone cannot make now that `pinnedCount` is
  // gone, and getting it wrong would yank the user off the tab every time a
  // filter excluded their pins.
  const unpinnedSinceRefetchRef = useRef(false);

  // Pins are server-side (EAI-1765): `getLeads` stamps each row with
  // `isPinned` and narrows to the pin set when the Pinned tab sends
  // `isPinned: true`. Every write goes through the hook's mutations, and what
  // happens next depends on the tab — see onChanged below.

  // Stamp the rows a pin write just changed, instead of re-running getLeads to
  // learn one boolean. Writes to the shared (redux) list, so the sidebar and
  // the mobile drawer both pick it up — a per-instance cache would leave
  // whichever list didn't perform the write showing the old icon.
  const applyPinStateToLeadList = res => {
    const pinned = new Set((res?.pinnedIds || []).map(Number));
    const unpinned = new Set((res?.unpinnedIds || []).map(Number));
    if (!pinned.size && !unpinned.size) return;
    setLeadList(
      (leadList || []).map(item => {
        const id = Number(item?.id);
        if (pinned.has(id)) return { ...item, isPinned: true };
        if (unpinned.has(id)) return { ...item, isPinned: false };
        return item;
      })
    );
  };

  const { isPinned, togglePin, pinMutationLoading, unpinAll } = usePinnedLeads({
    leads: leadData,
    locationId: sessionStorage.getItem("currentLocationId"),
    onChanged: res => {
      // `unpinnedIds` comes back populated for both unpin paths — single and
      // "unpin all" — and is empty for a pin.
      const unpinnedCount = (res?.unpinnedIds || []).length;
      onPinsChanged();

      // Every tab but Pinned runs the unnarrowed query, where a pin write
      // changes exactly one boolean on one row: membership is untouched (no
      // isPinned arg), ordering is untouched (the server dropped the pin-first
      // sort key), `count` is untouched, and `unreadLeadsCount` is untouched
      // because pinning doesn't read a message. Refetching there spent a full
      // getLeads — a CTE, two window functions and that whole selection set —
      // to flip a flag we were handed in the mutation result. The refetch was
      // load-bearing only because `isPinned` is read off the row and the rows
      // come from the query; patching them locally removes the need.
      if (!isPinnedTab) {
        applyPinStateToLeadList(res);
        return;
      }

      // On the Pinned tab the rows themselves change, so the query has to
      // re-run — except when this unpin emptied the tab, and here we already
      // know it did: `pinnedInScope` is what the tab holds and the response
      // says how many of them just left. Switching to All IS the refetch (it
      // rewrites `isPinned` off the variables, which fires the unnarrowed
      // query), so going through refetchLeads first would spend a request
      // fetching an empty pinned page and flash "No pinned leads" on the way
      // out. Covers "unpin all" too, which clears every pin by definition.
      if (unpinnedCount > 0 && pinnedInScope - unpinnedCount <= 0) {
        onLeadTabChange(LEAD_TABS.ALL);
        return;
      }

      // Still rows left — or so we think. The flag lets the effect below catch
      // the case where the refetch disagrees, e.g. another session unpinned
      // the rest concurrently.
      if (unpinnedCount > 0) {
        unpinnedSinceRefetchRef.current = true;
      }
      refetchLeads();
    },
  });

  // Clears every pin in the location scope in one mutation, so a user with
  // dozens doesn't have to unpin them one card at a time. Confirmed first —
  // it's bulk, and there's no undo.
  const confirmUnpinAll = () => {
    Modal.confirm({
      title: "Unpin all leads?",
      icon: <ExclamationCircleFilled />,
      content: (
        <div>
          {/* Deliberately no number: the mutation sweeps every permitted
              rooftop, while the Pinned tab only ever shows the current
              location scope narrowed by the active filters. Any count we could
              put here would understate what the button actually clears. */}
          This removes every lead from your Pinned tab, in all of your locations. The leads
          themselves aren&apos;t changed, and you can pin them again at any time.
        </div>
      ),
      okText: "Unpin all",
      onOk() {
        // The fall-back to All is handled uniformly in onChanged above — this
        // clears every pin, so the "did this empty the tab" test there is
        // always true.
        return unpinAll(lang);
      },
      onCancel() {},
    });
  };

  // Leads brought over by Lead Center's "Apply to Activity Center". These are
  // transient (page state in engagements.js, gone on refresh) and stay a
  // separate collection from the persisted pins above — same purpose, different
  // lifetime, so they get their own tab rather than being merged.
  const pinnedFromLcLeads = useMemo(
    () => (selectedLeadsFromLeadCenter || []).filter(item => item?.id != null),
    [selectedLeadsFromLeadCenter]
  );

  // The two pinned tabs are resolved client-side (by-id fetch / page state), so
  // `getLeads`-backed search can't narrow them. Filter them here instead —
  // otherwise typing a search visibly narrows All while the pinned tabs sit
  // unchanged.
  const filterBySearch = useCallback(
    rows => {
      const term = (search || "").trim().toLowerCase();
      if (!term) return rows;
      return (rows || []).filter(item =>
        [item?.fullName, item?.full_name, item?.name, item?.phone, item?.email]
          .filter(Boolean)
          .some(field => String(field).toLowerCase().includes(term))
      );
    },
    [search]
  );

  // "Pinned from LC" only exists right after the user applied leads from Lead
  // Center, so it stays hidden rather than sitting at a permanent (0).
  const showPinnedFromLcTab = showLeadTabs && pinnedFromLcLeads.length > 0;

  // Applying leads from Lead Center is a request to look at exactly those
  // leads, so land on their tab instead of dropping the user on All to hunt
  // for it (EAI-1800). Only the bulk "Apply to Activity Center" action counts:
  // opening a single row from Lead Center also fills this collection, and
  // switching there would replace the whole list with that one row.
  //
  // Keyed on the applied set rather than on "is it non-empty", so the effect
  // fires once per apply and the user stays wherever they move afterwards.
  // The key is cleared when the collection empties, so re-applying the same
  // leads later still reads as a new apply.
  const lcLeadsKey = useMemo(
    () => pinnedFromLcLeads.map(item => String(item.id)).join(","),
    [pinnedFromLcLeads]
  );
  const appliedLcLeadsKeyRef = useRef(null);
  useEffect(() => {
    if (!lcLeadsKey) {
      appliedLcLeadsKeyRef.current = null;
      return;
    }
    if (!showPinnedFromLcTab || !isLeadCenterApply) return;
    if (appliedLcLeadsKeyRef.current === lcLeadsKey) return;
    appliedLcLeadsKeyRef.current = lcLeadsKey;
    setIsLcTabActive(true);
  }, [lcLeadsKey, showPinnedFromLcTab, isLeadCenterApply]);

  const activeTabLeads = useMemo(() => {
    if (!showLeadTabs) return leadData;
    // Pinned is served by the same query with `isPinned: true`, so leadData is
    // ALREADY the pinned page — filtering it again here would only re-apply a
    // narrower client view over one page of an otherwise correct result.
    if (activeLeadTab === LEAD_TABS.PINNED_FROM_LC) return filterBySearch(pinnedFromLcLeads);
    return leadData;
  }, [showLeadTabs, activeLeadTab, leadData, pinnedFromLcLeads, filterBySearch]);

  // Tabs backed by `getLeads` — everything except the transient LC collection.
  // They share the server's loading state, count and pagination.
  const isServerBackedTab = !showLeadTabs || activeLeadTab !== LEAD_TABS.PINNED_FROM_LC;

  // The tabs are not always on screen: a ?leadId= deep link hides them, and
  // browser-extension-v4 also hides them for any active search
  // (`isSingleLeadView={isSingleLeadView || !!search}`). A Pinned narrowing
  // left on the variables would then keep restricting the list to pinned leads
  // with nothing on screen saying so and no control to undo it — a search that
  // silently returns only pinned matches. Drop it when the tabs go away.
  useEffect(() => {
    if (!showLeadTabs && variables?.isPinned) {
      setVariables({ ...variables, page: 1, isPinned: undefined });
    }
  }, [showLeadTabs, variables?.isPinned]);

  // The LC tab can empty out (the user removed the last applied lead) while
  // it's the active tab — fall back to All rather than showing a dead view.
  useEffect(() => {
    if (activeLeadTab === LEAD_TABS.PINNED_FROM_LC && !showPinnedFromLcTab) {
      onLeadTabChange(LEAD_TABS.ALL);
    }
  }, [activeLeadTab, showPinnedFromLcTab]);

  // Safety net for the same fall-back onChanged does up front. It handles the
  // unpin that we expected to leave rows behind but didn't — the count we
  // subtracted from was a page old, or another session unpinned the rest
  // first — so the tab still comes back empty and the user should not be left
  // standing in it.
  //
  // Gated on an actual unpin, not on the tab merely being empty: clicking
  // straight into an empty Pinned tab should show its "No pinned leads"
  // caption, and a filter that excludes every pin should show "No lead match
  // your filters" — neither is a reason to move the user somewhere else.
  useEffect(() => {
    // Drop a flag raised by an unpin the user has already navigated away from,
    // so it can't fire on some later visit to the tab.
    if (!isPinnedTab) {
      unpinnedSinceRefetchRef.current = false;
      return;
    }
    // Wait for the refetch: mid-flight the list still holds the old rows.
    if (leadsLoading || !unpinnedSinceRefetchRef.current) return;
    unpinnedSinceRefetchRef.current = false;
    if (pinnedInScope === 0) {
      onLeadTabChange(LEAD_TABS.ALL);
    }
  }, [isPinnedTab, pinnedInScope, leadsLoading]);

  const leadTabItems = useMemo(() => {
    // Unread pill: red regardless of which tab is active, and absent at zero —
    // a red "0" reads as an alert for nothing.
    const unreadPill = count =>
      count > 0 ? (
        <span className="ml-1.5 inline-flex items-center justify-center min-w-[16px] px-1 h-[16px] rounded-full text-[10px] font-[500] leading-none bg-[#FF4D4F] text-white">
          {count > 99 ? "99+" : count}
        </span>
      ) : null;
    const tab = (key, label, badge) => ({
      key,
      label: (
        <span className="flex items-center">
          {label}
          {badge}
        </span>
      ),
    });
    return [
      // Server aggregate only. `unreadLeadsCount` counts LEADS carrying unread
      // across the whole filtered set — every page — scoped by the same
      // arguments as the tab's own query, so All counts the filtered set and
      // Pinned counts the pinned subset. Nothing is derived from the loaded
      // rows here: that only ever saw one page and drifted as you paginated.
      // One query answers only for the scope it ran with, so the inactive tab
      // shows the value from the last time it WAS active (undefined until
      // then, which renders no pill) rather than costing a second request.
      // See engagementActivity's onCompleted.
      tab(LEAD_TABS.ALL, lang.all, unreadPill(unreadLeadsCount?.[LEAD_TABS.ALL])),
      tab(LEAD_TABS.PINNED, lang.pinned, unreadPill(unreadLeadsCount?.[LEAD_TABS.PINNED])),
      // No badge: this tab is transient page state, so getLeads never counts
      // it, and the FE no longer derives unread totals from loaded rows — that
      // only ever counted the current page.
      ...(showPinnedFromLcTab
        ? [tab(LEAD_TABS.PINNED_FROM_LC, lang.pinnedFromLeadCenter, null)]
        : []),
    ];
  }, [lang, unreadLeadsCount, showPinnedFromLcTab]);

  const scrollListToTop = () => {
    if (listContainerRef.current) {
      listContainerRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const { loading: opportunityStatusLoader } = useQuery(GET_OPPORTUNITY_STATUS, {
    variables: {
      fetchAll: true,
      locationIds: (sessionStorage?.getItem("currentLocationId") || "").includes(",")
        ? sessionStorage
            ?.getItem("currentLocationId")
            ?.split(",")
            .map(el => +el)
        : [+sessionStorage?.getItem("currentLocationId")],
    },
    onCompleted: resp => {
      setOpportunityStatusList(resp.getOpportunityStatus || []);
    },
  });

  const [setLeadAsNoResponseNeeded] = useMutation(UPDATE_LEAD_ARCHIVE_STATUS, {
    onCompleted: resp => {
      setArchivingLeadId(null);
      if (resp?.updateLeadArchiveStatus?.statusCode === 200) {
        message.success(resp?.updateLeadArchiveStatus?.message || "");
        setNoResponseNeededLoading(false);
        refetchLeads();
      } else {
        message.error(resp?.updateLeadArchiveStatus?.message || "");
      }
    },
    onError: () => {
      setArchivingLeadId(null);
    },
  });
  const handleVisibleChange = () => {
    setVisible(!visible);
  };

  const clearUrl = () => {
    if (window.location.search) {
      if (window.location.pathname?.includes("browser-ext")) {
        window.history.pushState("", "", "/browser-ext");
      } else {
        window.history.pushState("", "", "/engagements/activity-center");
      }
    }
  };

  // Date tab counts as "1" when any selection is committed (preset OR
  // complete custom range) — mirrors the per-tab `dateFilterCount` in
  // ActivityCenterFilter.
  const dateFilterCount =
    dateFilterType || (Array.isArray(leadDateRange) && leadDateRange.length === 2) ? 1 : 0;

  // Dedupe the status set — `selectedStatus` (local) and
  // `selectedQuickFilters` (redux) can hold the same ids (the BE payload
  // already unions them via `uniq([...selectedQuickFilters, ...selectedStatus])`).
  // Count once per unique id so the badge matches the per-tab chips.
  //
  // A force-applied MY_CONVERSATIONS (the restrict-remove-default permission)
  // IS counted: it's an active filter, the Quick filters tab shows it in its
  // own count, and excluding it here made the badge disagree with the panel
  // (badge "1" next to Quick filters (1) + Teams (1)).
  const statusUnion = new Set([...(selectedQuickFilters || []), ...(selectedStatus || [])]);

  const filterCount =
    (reduxCampaignFilters || []).length +
    (variables.search && variables.search !== "undefined" ? 1 : 0) +
    (presetLeadFilter && presetLeadFilter !== "undefined" ? 1 : 0) +
    statusUnion.size +
    (userFilters || []).length +
    (teamFilters || []).length +
    dateFilterCount +
    (opportunityTypeFilterCount || 0) +
    (opportunityStatusFilterCount || 0) +
    (opportunitySourceFilterCount || 0);

  // Empty-list caption. Filters take precedence (they're the actionable
  // reason), then a single-lead view that came back with nothing, otherwise
  // the plain generic case.
  const emptyListMessage = (() => {
    // Pinned IS a getLeads query now, so an active filter or search can empty
    // it while the user still has pins. Without a standalone pin total the two
    // cases are indistinguishable from the response alone — so let the filters
    // name the actionable reason, and keep "no pinned leads" for the case
    // where nothing is narrowing the list.
    if (isPinnedTab) {
      if (filterCount > 0) return "No lead match your filters";
      return lang.noPinnedLeads;
    }
    // The LC collection is page state — filter/single-lead captions would be
    // misleading there, those rows are never filtered by `getLeads`.
    if (activeLeadTab === LEAD_TABS.PINNED_FROM_LC && showLeadTabs) return "No data";
    if (filterCount > 0) return "No lead match your filters";
    if (isSingleLeadView) return "No lead found";
    return "No data";
  })();

  useEffect(() => {
    if (newLead !== null && addnewLead) {
      const array = () => {
        const temp = structuredClone(leads);
        newLead.fullName = newLead.full_name;
        temp.unshift(newLead);
        return [...temp];
      };
      setLeadList(array());
      setAddNewLead(false);
      setNewLead(null);
    }
  }, [newLead, addnewLead]);

  useEffect(() => {
    setSearchKey(selectedLeadPhoneReduxData);
  }, [selectedLeadPhoneReduxData]);

  useEffect(() => {
    if (!window.location.search) dispatch(setSelectedLeadPhone({ selectedLeadPhone: searchKey }));
    // return () => dispatch(setSelectedLeadPhone({ selectedLeadPhone: "" }));
  }, [searchKey]);

  useEffect(() => {
    return () => dispatch(setSelectedLeadPhone({ selectedLeadPhone: "" }));
  }, []);

  useEffect(() => {
    setLeadData(leadList);
  }, [leadList]);

  const setSearch = search => {
    setVariables({ ...variables, page: 1 });
    dispatch(setSearchRedux(search));
    scrollListToTop();
  };
  const SortOptionsContainer = () => {
    const [sortOption, setsortOption] = useState(leadSort);
    // prioritySortOrder
    return (
      <>
        <h3>Sort options</h3>
        <Radio.Group
          value={sortOption}
          onChange={e => {
            setsortOption(e.target.value);
            dispatch(setLeadSortOrder(e.target.value));
            scrollListToTop();
          }}
          className="flex flex-col"
        >
          <Radio value="default">
            {PROJECT_NAME_V2} Sort{" "}
            <Tooltip title="Sorts leads that require immediate action, including pending replies and follow-ups, to the top.">
              <InfoCircleOutlined className="icon-color" />
            </Tooltip>{" "}
          </Radio>
          <Radio value="lastMessage">
            Most Recent Message{" "}
            <Tooltip placement="bottom" title="Sort by latest activities">
              <InfoCircleOutlined className="icon-color" />
            </Tooltip>{" "}
          </Radio>
        </Radio.Group>
      </>
    );
  };

  // Single source of truth — chip classes come from the shared
  // quickFilterColors map so the dropdown chips, the per-status pills, and
  // the ActivityCenterFilter > Quick filters dots all stay in sync.
  const backgroundColor = id => getQuickFilterChipClass(id);

  const canMergeOppor = checkAccessPermission(AccessPermissionAction.MERGE_LEADS_OPPS);
  const restrictRemoveDefaultFilter = checkAccessPermission(
    AccessPermissionAction.ACTIVITY_CENTER_RESTRICT_REMOVE_DEFAULT_FILTER
  );

  const isMyConversationRestricted = itemId =>
    restrictRemoveDefaultFilter && itemId === QUICK_FILTERS.MY_CONVERSATIONS;

  const renderFilters = leadStatus.map(item => ({
    label: (
      <Checkbox
        onClick={e => e.stopPropagation()}
        onChange={e => {
          if (isMyConversationRestricted(item.id) && !e.target.checked) return;
          if (setVariables) {
            setVariables({
              ...variables,
              leadId: null,
              page: 1,
            });
          }
          if (e.target.checked) {
            dispatch(addQuickFilter(item.id));
          } else {
            dispatch(removeQuickFilter(item.id));
          }
          scrollListToTop();
          clearUrl();
        }}
        checked={selectedQuickFilters.includes(item.id)}
        disabled={isMyConversationRestricted(item.id) && selectedQuickFilters.includes(item.id)}
      >
        <span onClick={e => e.stopPropagation()} className={`${backgroundColor(item.id)}`}>
          {item.name}
        </span>
      </Checkbox>
    ),
    key: item.id,
  }));

  // Same row renderer for both the pinned section and the main list, so a
  // pinned card is pixel-identical to an unpinned one.
  const renderLeadItem = item => {
    // The pin link is tab-scoped. On "Pinned from LC" it detaches the lead
    // from that transient list only (leaving any persisted pin alone), so it
    // always reads "Unpin"; on every other tab it toggles the persisted pin.
    const isLcTab = showLeadTabs && activeLeadTab === LEAD_TABS.PINNED_FROM_LC;
    const pinLinkPinned = isLcTab || isPinned(item);
    const onPinLinkClick = isLcTab ? () => handleUnpinLead(item) : () => togglePin(item, lang);

    return (
      <ListItem
        item={item}
        selectedLead={selectedLead}
        setSummaryVisible={setSummaryVisible}
        user={user}
        isFromChatbot={isFromChatbot}
        sourcesData={sourcesData}
        activeOpp={activeOpp}
        setActiveOpp={oppr => {
          setActiveOpp(oppr);
          dispatch(changeMessageOpportunity(oppr));
        }}
        openAppointmentPopup={openAppointmentPopup}
        handleAppointmentModalClose={handleAppointmentModalClose}
        spinner={spinner}
        refetchLeads={refetchLeads}
        setAttachments={setAttachments}
        setmessageText={setmessageText}
        setSelectedLead={setSelectedLead}
        changePage={changePage}
        messageFilters={messageFilters}
        setMessageFilters={setMessageFilters}
        setShowLeadSection={setShowLeadSection}
        setOpportunityList={setOpportunityList}
        setFilterApplied={setFilterApplied}
        setSelectedLeadId={setSelectedLeadId}
        timeReference={timeReference}
        setbtnClicked={setbtnClicked}
        handleLeadListDrawer={handleLeadListDrawer}
        isLeadPinned={pinLinkPinned}
        onTogglePin={onPinLinkClick}
        lang={lang}
        setLeadAsNoResponseNeeded={setLeadAsNoResponseNeeded}
        setNoResponseNeededLoading={setNoResponseNeededLoading}
        archivingLeadId={archivingLeadId}
        setArchivingLeadId={setArchivingLeadId}
        getDuplicateLeads={getDuplicateLeads}
        getMergedLead={getMergedLead}
        removeMergedLead={removeMergedLead}
        showAll={showAll}
        leadData={leadData}
        setShowAll={setShowAll}
        canMergeOppor={canMergeOppor}
        opportunityStatusList={opportunityStatusList}
        selectedOppTypeId={selectedOppTypeId}
        setSelectedOppTypeId={setSelectedOppTypeId}
        listFilterRootOppTypeIds={listFilterRootOppTypeIds}
      />
    );
  };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      <TopLeadFilters
        lang={lang}
        search={search}
        isFromChatbot={isFromChatbot}
        setSearchKey={setSearchKey}
        setQueryString={setQueryString}
        handleSearch={handleSearch}
        setSearch={setSearch}
        SortOptionsContainer={SortOptionsContainer}
        visible={visible}
        handleVisibleChange={handleVisibleChange}
        variables={variables}
        setVariables={setVariables}
        opportunityList={opportunityList}
        activeOpp={activeOpp}
        handleOppFilter={handleOppFilter}
        setActiveOpp={setActiveOpp}
        setShowLeadSection={setShowLeadSection}
        selectedStatus={selectedStatus}
        setSelectedStatus={setSelectedStatus}
        leadStatus={leadStatus}
        handleStatusFilter={handleStatusFilter}
        campaignFilters={campaignFilters}
        queryString={queryString}
        entityId={entityId}
        eventId={eventId}
        filterCount={filterCount}
        renderFilters={renderFilters}
        dispatch={dispatch}
        selectedQuickFilters={selectedQuickFilters}
        backgroundColor={backgroundColor}
        scrollListToTop={scrollListToTop}
        clearUrl={clearUrl}
        restrictRemoveDefaultFilter={restrictRemoveDefaultFilter}
        onClearAllFilters={onClearAllFilters}
        leadTabs={
          showLeadTabs ? (
            <Tabs
              className="lead-list-tabs"
              activeKey={activeLeadTab}
              // Tab switching only swaps the rendered collection — `selectedLead`
              // is deliberately left alone so the conversation pane doesn't
              // reload a different lead on every tab click.
              onChange={key => {
                // Switching to/from Pinned rewrites `isPinned` on the getLeads
                // variables and resets to page 1, so the server does the
                // filtering — see onLeadTabChange.
                onLeadTabChange(key);
                scrollListToTop();
              }}
              items={leadTabItems}
              size="small"
            />
          ) : null
        }
      />

      <div ref={listContainerRef} className="overflow-y-auto flex-grow list-border-bottom">
        {/* Bulk clear for the Pinned tab. Hidden when the tab is empty —
            there is then nothing on screen for it to plausibly act on. */}
        {isPinnedTab && pinnedInScope > 0 && (
          <div className="flex justify-end px-2 pt-2">
            <Button
              type="link"
              size="small"
              className="!px-0 !text-xs"
              disabled={pinMutationLoading}
              onClick={confirmUnpinAll}
            >
              Unpin all
            </Button>
          </div>
        )}
        <List
          className="cursor-pointer"
          itemLayout="horizontal"
          loading={(isServerBackedTab ? leadsLoading : false) || pinMutationLoading}
          // antd's default "No data" doesn't say WHY the list is empty. Name the
          // reason when we know it — the active tab, active filters, or a
          // single-lead (?leadId=) view that returned nothing.
          locale={{
            emptyText: <CustomEmpty description={emptyListMessage} />,
          }}
          dataSource={activeTabLeads}
          renderItem={renderLeadItem}
        />
        {/* "Show all leads" widens the MAIN list — it drops the single-lead
            view and, in the extension, the CRM scoping with it. That only
            means anything on All. On Pinned it would offer to replace the
            user's pins with everything, and on "Pinned from LC" it would
            discard a collection this button knows nothing about. Kept for the
            no-tabs case, which is the All list by definition. */}
        {isSingleLeadView && (!showLeadTabs || activeLeadTab === LEAD_TABS.ALL) && (
          <div className="flex justify-center py-2 px-4">
            <Button type="link" className="!font-medium" block onClick={onLoadAllLeads}>
              Show all leads
            </Button>
          </div>
        )}
      </div>

      {/* Pinned paginates through the same query, so it gets the same pager —
          `leadCount` already counts only pinned leads when it is active. */}
      {isServerBackedTab && !isEmpty(leadData) && leadCount > 5 && !leadsLoading && (
        <div className="pt-3 pb-2 sticky-pagination">
          <Pagination
            current={variables.page}
            pageSize={variables.pageSize}
            total={leadCount}
            align="center"
            pageSizeOptions={["5", "25", "50"]}
            showSizeChanger
            onChange={(page, pageSize) => {
              const newVariables = {
                ...variables,
                page,
                pageSize,
              };
              localStorage.removeItem("clickLeadId");
              setVariables(newVariables);
              setActiveOpp("#DEFAULTOPPID");
              handleScrollOnMovement();
              setSelectedLead({});
              localStorage.removeItem("changedLocationLeadId");
              setActiveLeadId(false);
              setLeadData([]);
            }}
            onShowSizeChange={(current, size) => {
              const newVariables = {
                ...variables,
                page: current,
                pageSize: size,
              };
              localStorage.removeItem("clickLeadId");
              setVariables(newVariables);
            }}
            size="small"
            className="!pb-1"
            showLessItems={window.location.pathname.includes("browser-ext") || width < 768}
          />
          <p className="text-center m-0 text-base font-normal">
            {lang.total}: {leadCount} {leadCount === 1 ? lang.lead : lang.leads}
          </p>
        </div>
      )}
    </div>
  );
};

export default LeadListContainer;
function TopLeadFilters({
  lang,
  search,
  isFromChatbot,
  setSearchKey,
  setQueryString,
  handleSearch,
  setSearch,
  SortOptionsContainer,
  visible,
  handleVisibleChange,
  variables,
  setVariables,
  opportunityList,
  activeOpp,
  handleOppFilter,
  setActiveOpp,
  setShowLeadSection,
  selectedStatus,
  setSelectedStatus,
  leadStatus,
  handleStatusFilter,
  campaignFilters,
  queryString,
  entityId,
  eventId,
  filterCount,
  renderFilters,
  dispatch,
  selectedQuickFilters,
  backgroundColor,
  scrollListToTop,
  clearUrl,
  restrictRemoveDefaultFilter,
  onClearAllFilters,
  leadTabs,
}) {
  const [localSearch, setLocalSearch] = useState(search || "");
  const lastDispatchedRef = useRef(search || "");

  // Search scope: true = "All Leads" (skipFilter on the BE), false = "Within
  // Filtered Lead". Lives in Redux so the getLeads query in engagementActivity
  // can read it directly.
  const searchAllLeads = useSelector(state => state.lead.searchAllLeads);
  // Dashboard drill-in banner flag — read here (inside `TopLeadFilters`)
  // because the banner renders in this component's JSX tree.
  const cameFromDashboard = useSelector(state => state.lead.cameFromDashboard);
  const [scopePopoverOpen, setScopePopoverOpen] = useState(false);

  // Location-level override: a location can grant "All Leads" search even to
  // users restricted to their own conversations, so the scope lock is the
  // permission MINUS that opt-in. Under "All locations" the flag is read from
  // the user's primary location (see useLocationHelpers).
  const { locationSettings } = useLocationHelpers();
  const allowAllLeadsSearchScope = Boolean(
    locationSettings?.[LOCATION_SETTING_KEYS.GENERAL_CONFIG]?.[
      GENERAL_CONFIG_KEYS.ALLOW_ALL_LEADS_SEARCH_SCOPE
    ]
  );
  // `locationSettings` is null until the location resolves — don't write state
  // off the flag before then (see the reset effect below).
  const isLocationSettingsResolved = locationSettings !== null;
  const isSearchScopeLocked = restrictRemoveDefaultFilter && !allowAllLeadsSearchScope;

  // Enables/disables "Clear all". `filterCount` counts every applied filter,
  // including the permission-forced MY_CONVERSATIONS — which Clear all re-adds
  // immediately, so on its own it leaves nothing for the action to do.
  const forcedQuickFilterCount =
    restrictRemoveDefaultFilter &&
    (selectedQuickFilters || []).includes(QUICK_FILTERS.MY_CONVERSATIONS)
      ? 1
      : 0;
  const hasRemovableFilters = (filterCount || 0) - forcedQuickFilterCount > 0;

  // When the workspace restricts users to their own conversations
  // (MY_CONVERSATIONS forced as a permission default via
  // restrictRemoveDefaultFilter), searching "All Leads" would bypass that
  // restriction because it sets skipFilter on the BE. Force the scope to
  // "Within Filtered Leads" and disable the "All Leads" option so a restricted
  // user can only ever search within their own leads — unless the location
  // opts back in via the General settings flag (`isSearchScopeLocked`).
  // Only force the scope down once the location settings have actually loaded —
  // before that `allowAllLeadsSearchScope` reads false for everyone, and the
  // reset would clobber the persisted "All Leads" choice of a restricted user
  // whose location does allow it. The disabled radio (below) is NOT gated the
  // same way: locked-until-proven-otherwise is the safe default for the UI.
  useEffect(() => {
    if (isLocationSettingsResolved && isSearchScopeLocked && searchAllLeads) {
      dispatch(setSearchAllLeads(false));
    }
  }, [isLocationSettingsResolved, isSearchScopeLocked, searchAllLeads, dispatch]);

  const searchScopeOptions = isSearchScopeLocked
    ? SEARCH_SCOPE_OPTIONS.map(option =>
        option.value === SEARCH_SCOPE.ALL
          ? {
              ...option,
              disabled: true,
              tooltip: "You can only search within your own conversations",
            }
          : option
      )
    : SEARCH_SCOPE_OPTIONS;

  const handleSearchScopeChange = e => {
    const isAll = e.target.value === SEARCH_SCOPE.ALL;
    // Restricted users cannot switch to "All Leads".
    if (isAll && isSearchScopeLocked) return;
    dispatch(setSearchAllLeads(isAll));
    // Close the popover once a scope is chosen.
    setScopePopoverOpen(false);
    // If a search is active, re-run it so the new scope takes effect now.
    const trimmed = localSearch?.trim() || "";
    if (trimmed.length >= MIN_SEARCH_LENGTH) {
      handleSearch(trimmed);
      scrollListToTop();
    }
  };

  const searchScopeContent = (
    <div className="w-[260px] py-1">
      <p className="px-1 m-0 mb-2 text-xs font-semibold tracking-wide text-black-45 uppercase">
        Search Scope
      </p>
      <CustomRadioGroup
        options={searchScopeOptions}
        value={searchAllLeads ? SEARCH_SCOPE.ALL : SEARCH_SCOPE.FILTERED}
        onChange={handleSearchScopeChange}
      />
    </div>
  );

  // Sync local state when Redux search changes externally (e.g., URL navigation)
  useEffect(() => {
    if (search !== lastDispatchedRef.current) {
      setLocalSearch(search || "");
      lastDispatchedRef.current = search || "";
    }
  }, [search]);

  const debouncedSearch = useDebounce(value => {
    const trimmed = value?.trim() || "";
    // Only trigger API if >= MIN_SEARCH_LENGTH chars, or empty (to clear results)
    if (trimmed.length >= MIN_SEARCH_LENGTH) {
      if (lastDispatchedRef.current !== trimmed) {
        lastDispatchedRef.current = trimmed;
        handleSearch(trimmed);
        scrollListToTop();
      }
    } else if (trimmed.length === 0 && lastDispatchedRef.current !== "") {
      lastDispatchedRef.current = "";
      handleSearch("");
      scrollListToTop();
    }
  }, SEARCH_DEBOUNCE_MS);

  const updateVariableContext = () => {
    if (setVariables) {
      setVariables({
        ...variables,
        leadId: null,
        page: 1,
      });
    }
  };

  return (
    <div className="sticky top-0 bg-white z-[999]">
      {cameFromDashboard && (
        <div className="pb-1">
          <CustomAlert
            type="info"
            showIcon
            closable
            renderDefaultMessage={false}
            className="!py-1 !px-2 [&_.ant-alert-message]:!leading-snug [&_.ant-alert-icon]:!mr-2"
            message={
              <span className="text-xs leading-snug">
                Showing all matching records, including those assigned to deactivated users. The
                dashboard excludes deactivated users, so this total may be higher.
              </span>
            }
            onClose={() => dispatch(setCameFromDashboard(false))}
          />
        </div>
      )}
      <div className="flex items-center">
        <Input
          data-testid="lead-search-input"
          size="large"
          placeholder={lang.searchLeadBy}
          name="search"
          className="z-10 bg-white"
          // Keep `suffix` always present (stable wrapper) so antd doesn't
          // remount the <input> when the clear icon appears/disappears — that
          // remount was stealing focus after the first character.
          suffix={
            <span data-testid="lead-search-clear">
              {!isEmpty(localSearch) && (
                <LuX
                  onClick={() => {
                    isFromChatbot && window.parent.postMessage(localSearch, "*");
                    setLocalSearch("");
                    lastDispatchedRef.current = "";
                    setSearchKey("");
                    setQueryString("");
                    handleSearch("");
                    setSearch("");
                    localStorage.setItem("previousLeadSearchTrack", "");
                  }}
                  className="cursor-pointer mb-[-3px] text-black-65 text-lg mr-[2px]"
                />
              )}
            </span>
          }
          // Scope toggle lives in its own bordered cell (antd `addonAfter`) so
          // it reads as a distinct control, separated from the search field.
          addonAfter={
            <Popover
              trigger="click"
              placement="bottomRight"
              content={searchScopeContent}
              overlayInnerStyle={{ padding: "10px 12px" }}
              open={scopePopoverOpen}
              onOpenChange={setScopePopoverOpen}
            >
              <span
                data-testid="lead-search-scope"
                className="flex items-center cursor-pointer"
                onClick={e => e.stopPropagation()}
              >
                <Tooltip title="Search scope">
                  {searchAllLeads ? (
                    <PiUserCircle className="text-[18px] text-black" />
                  ) : (
                    <PiUserCircleDashed className="text-[18px] text-black" />
                  )}
                </Tooltip>
              </span>
            </Popover>
          }
          value={localSearch}
          onChange={e => {
            const { value } = e.target;
            setLocalSearch(value);

            if (isEmpty(value)) {
              if (window.location.pathname.includes("browser-ext")) {
                window.history.pushState("", "", `/browser-ext/`);
              } else {
                window.history.pushState("", "", `/engagements/activity-center`);
              }
            }
            if (isFunction(clearUrl)) {
              clearUrl();
            }
            debouncedSearch(value);
          }}
        />
        <Popover trigger="click" placement="right" content={<SortOptionsContainer />}>
          <Button className="mx-1 flex items-center gap-2 h-[39px]">
            {/* {!isBrowserExt && "Sort"} */}
            <SortIcon />
          </Button>
        </Popover>
        <Popover
          placement="right"
          open={visible}
          onOpenChange={handleVisibleChange}
          content={
            <ActivityCenterFilter
              variables={variables}
              setVariables={setVariables}
              scrollListToTop={scrollListToTop}
              clearUrl={clearUrl}
              restrictRemoveDefaultFilter={restrictRemoveDefaultFilter}
              handleVisibleChange={handleVisibleChange}
              onClearAllFilters={onClearAllFilters}
            />
          }
          trigger="click"
          overlayInnerStyle={{ padding: 0 }}
        >
          <Tooltip placement="topLeft" title={lang.moreLeadFilters}>
            <Button className="flex items-center gap-2 h-[39px]">
              {/* {!isBrowserExt && "Filter"} */}
              <Badge count={filterCount} className="mt-2">
                <FilterIcon />
              </Badge>
            </Button>
          </Tooltip>
        </Popover>
      </div>
      {leadTabs}
      <div className="flex-col-centered gap-1">
        <div className="flex items-center my-1 justify-between w-full">
          <Dropdown
            menu={{
              items: renderFilters,
            }}
            trigger="click"
          >
            <div className="flex-row-centered gap-2 cursor-pointer items-center">
              <CustomLabel label={lang.quickFilters} labelClass="font-normal text-[14px]" />{" "}
              <DownIcon />
            </div>
          </Dropdown>
          <Button
            // Nothing removable → nothing to clear. `filterCount` includes the
            // permission-forced MY_CONVERSATIONS (which this handler re-adds
            // straight after the reset), so that one is discounted here.
            disabled={!hasRemovableFilters}
            onClick={() => {
              if (setVariables) {
                setVariables({
                  ...variables,
                  leadId: null,
                  page: 1,
                  campaignIds: [],
                });
              }
              dispatch(resetLeadFilters());
              if (restrictRemoveDefaultFilter) {
                dispatch(addQuickFilter(QUICK_FILTERS.MY_CONVERSATIONS));
              }
              scrollListToTop?.();
              clearUrl?.();
              onClearAllFilters?.();
            }}
            type="text"
            className={hasRemovableFilters ? "text-blue-500" : "text-grey-300"}
          >
            Clear all
          </Button>
        </div>
        {selectedQuickFilters.length > 0 && (
          <div className="flex-row-centered mb-1 mr-1">
            {(selectedQuickFilters || []).slice(0, 2).map((item, index) => {
              const filter = leadStatus.find(status => status.id === item);
              return (
                <Tag
                  className={`cursor-pointer flex-row-centered gap-1 border-none ${backgroundColor(
                    filter?.id
                  )}`}
                  onClose={() => {
                    if (
                      restrictRemoveDefaultFilter &&
                      filter?.id === QUICK_FILTERS.MY_CONVERSATIONS
                    )
                      return;
                    updateVariableContext();
                    dispatch(removeQuickFilter(filter?.id));
                    if (filter?.id === QUICK_FILTERS.MY_CONVERSATIONS)
                      dispatch(removeMyConversationFlag());
                  }}
                  key={index}
                >
                  <span>{filter?.name}</span>{" "}
                  {!(
                    restrictRemoveDefaultFilter && filter?.id === QUICK_FILTERS.MY_CONVERSATIONS
                  ) && (
                    <CloseOutlined
                      className="cursor-pointer"
                      onClick={() => {
                        updateVariableContext();
                        dispatch(removeQuickFilter(filter?.id));
                        if (filter?.id === QUICK_FILTERS.MY_CONVERSATIONS)
                          dispatch(removeMyConversationFlag());
                        scrollListToTop?.();
                        clearUrl?.();
                        onClearAllFilters?.();
                      }}
                    />
                  )}
                </Tag>
              );
            })}
            {(selectedQuickFilters || []).length > 2 && (
              <Dropdown
                menu={{
                  items: renderFilters,
                }}
                trigger="click"
              >
                <Tag className="cursor-pointer">+ {(selectedQuickFilters || []).length - 2}</Tag>
              </Dropdown>
            )}
          </div>
        )}
        <div className="h-[1px] mb-1 w-full bg-grey-200" />
      </div>
    </div>
  );
}

const findLastMessage = (messages, n = 0) => {
  // Base case: If the index is out of range, return null.
  if (n >= messages.length) return null;
  const message = messages[n];
  // Check if the message is valid, has non-empty content, and is something a
  // person actually said. A consent change or merge carries a JSON audit
  // payload in `content`, which would render as the preview verbatim.
  if (
    message !== undefined &&
    message !== null &&
    message?.content !== "" &&
    isConversationMessage(message)
  ) {
    return message;
  }
  // Recursively check the next message.
  return findLastMessage(messages, n + 1);
};

function ListItem({
  item,
  selectedLead,
  setSummaryVisible,
  user,
  isFromChatbot,
  sourcesData,
  activeOpp,
  setActiveOpp,
  openAppointmentPopup,
  handleAppointmentModalClose,
  spinner,
  refetchLeads,
  setAttachments,
  setmessageText,
  setSelectedLead,
  changePage,
  messageFilters,
  setMessageFilters,
  setShowLeadSection,
  setOpportunityList,
  setFilterApplied,
  setSelectedLeadId,
  timeReference,
  setbtnClicked,
  handleLeadListDrawer,
  lang,
  setNoResponseNeededLoading,
  setLeadAsNoResponseNeeded,
  archivingLeadId,
  setArchivingLeadId,
  isLeadPinned = false,
  onTogglePin = () => {},
  getDuplicateLeads = () => {},
  getMergedLead = () => {},
  removeMergedLead = () => {},
  showAll,
  leadData,
  setShowAll,
  canMergeOppor,
  opportunityStatusList,
  selectedOppTypeId,
  setSelectedOppTypeId = () => {},
  listFilterRootOppTypeIds = [],
}) {
  const dispatch = useDispatch();
  // Hover reveals the row's inline actions (Archive + Pin). "Unpin" and
  // "Restore" stay visible without hover — they're the only cue that the row is
  // pinned / archived.
  const [isRowHovered, setIsRowHovered] = useState(false);
  // True while this row's "Show All" merge fetch is in flight, so the banner can
  // show "Loading..." instead of flipping to "Close" before the data arrives.
  const [isMergeLoading, setIsMergeLoading] = useState(false);

  const handleToggleMerge = async () => {
    const isShowAll = showAll?.[item?.id] ?? true;
    if (!isShowAll) {
      // Currently expanded → collapse immediately (no API call).
      removeMergedLead(item?.mergeHistory, leadData);
      setShowAll(prev => ({ ...prev, [item?.id]: true }));
      return;
    }
    // Collapsed → fetch + append, holding "Loading..." until it resolves. Only
    // flip to "Close" on success so a failed fetch stays on "Show All".
    setIsMergeLoading(true);
    try {
      await getMergedLead(item, item?.mergeHistory, leadData);
      setShowAll(prev => ({ ...prev, [item?.id]: false }));
    } catch (error) {
      // Leave the banner on "Show All" so the user can retry; nothing was
      // appended on failure.
      console.error("[merge] failed to load merged leads", error);
    } finally {
      setIsMergeLoading(false);
    }
  };
  // Prefer the selected lead's freshly-fetched messages, but only once they've
  // actually loaded. While the server fetch is in flight selectedLead.messages is
  // empty — fall back to the list item's messages so we don't briefly flash
  // "No conversation yet" for the lead just clicked.
  const sortedMessages = (
    (selectedLead?.id === item?.id && selectedLead?.messages?.length > 0
      ? selectedLead?.messages
      : item?.messages) || []
  )
    .map(el => ({ ...el, recentDate: el?.dateReceived || el?.dateSent }))
    .sort((a, b) => {
      const dateA = dayjs(a?.recentDate);
      const dateB = dayjs(b?.recentDate);
      return dateB.isBefore(dateA) ? -1 : 1;
    });

  const lastMessage = findLastMessage(sortedMessages || [], 0);
  return (
    <>
      <List.Item
        key={item?.id}
        className={`leadBox border-solid border-l-[6px] border-[1px] mt-3 p-3 mr-2 rounded-[4px] ${
          selectedLead && +selectedLead?.id === +item?.id ? "selectedLeadClass" : "none"
        } ${item.isDeleted ? "bg-[#FEF2F2]" : ""}`}
        onMouseEnter={() => setIsRowHovered(true)}
        onMouseLeave={() => setIsRowHovered(false)}
        style={{
          // Strip color obeys product-spec priority — see
          // `resolveLeadStripStatus` at module scope.
          borderLeftColor: getQuickFilterDotColor(resolveLeadStripStatus(item)),
          // borderLeftColor: colorCode[item?.messageStatusType?.status]
          //   ? colorCode[item?.messageStatusType?.status]
          //   : "#22C55E",
        }}
        onClick={() => {
          setSummaryVisible &&
            setSummaryVisible({
              visible: false,
              count: 0,
            });
        }}
      >
        <List.Item.Meta
          title={
            <TitleComponent
              item={item}
              lang={lang}
              isRowHovered={isRowHovered}
              setNoResponseNeededLoading={setNoResponseNeededLoading}
              setLeadAsNoResponseNeeded={setLeadAsNoResponseNeeded}
              archivingLeadId={archivingLeadId}
              setArchivingLeadId={setArchivingLeadId}
              isLeadPinned={isLeadPinned}
              onTogglePin={onTogglePin}
              opportunityStatusList={opportunityStatusList}
              selectedOppTypeId={selectedOppTypeId}
              selectedLead={selectedLead}
              user={user}
            />
          }
          description={
            <LeadList
              messageObject={lastMessage}
              user={user}
              item={item}
              isFromChatbot={isFromChatbot}
              sourcesData={sourcesData}
              activeOpp={activeOpp}
              setActiveOpp={setActiveOpp}
              openAppointmentPopup={openAppointmentPopup}
              handleAppointmentModalClose={handleAppointmentModalClose}
              spinner={spinner}
              selectedLeadId={selectedLead?.id}
              refetchLeads={refetchLeads}
              selectedLead={selectedLead}
              selectedOppTypeId={selectedOppTypeId}
              listFilterRootOppTypeIds={listFilterRootOppTypeIds}
            />
          }
          onClick={() => {
            setAttachments([]);
            setmessageText("");
            if (+selectedLead?.id === +item?.id) {
              return;
            }
            // Different lead selected — any AI suggestion in the slice belongs
            // to the previous lead and is no longer valid. Clear both channels
            // so the next createMessage / createMms / createEmail doesn't
            // accidentally tag the new lead with the old lead's AI ID.
            dispatch(resetAiResponseHistoryIds());
            localStorage.getItem("changedLocationLeadId") &&
              localStorage.removeItem("changedLocationLeadId");
            // Seed the active tab from the NEW lead's most-recent
            // permitted opp BEFORE setSelectedLead fires. Uses the same
            // helper as the lead-card render so the row's data and the
            // detail panel's tab stay in sync.
            //
            // Always reset (not just when null) — otherwise the prior
            // lead's tab carries over to the next click. With the
            // selected-row exception in the card, that stale tab would
            // briefly drive the new row's content for one render cycle
            // (flicker) until the engagementActivity useEffect runs and
            // overwrites it with the new lead's correct tab. Setting
            // both atomically here eliminates the in-between render.
            const nextTab = getMostRecentPermittedRootOppTypeId(item, user);
            setSelectedOppTypeId(nextTab || null);
            setSelectedLead({ ...item });

            changePage();

            const dummyPayload = { ...messageFilters };
            delete dummyPayload.leadOpportunityId;
            setMessageFilters({
              ...dummyPayload,
              page: 1,
              leadId: item?.id,
              lastId: "",
            });
            setShowLeadSection(false);
            setOpportunityList([
              {
                filterId: null,
                id: "#DEFAULTOPPID",
                name: "All",
              },
            ]);
            setFilterApplied(false);
            setActiveOpp("#DEFAULTOPPID");
            setSelectedLeadId(item?.id);
            clearTimeout(timeReference);
            setbtnClicked(false);
            handleLeadListDrawer();
          }}
        />
      </List.Item>
      {item?.potentialLeads?.length > 0 && (
        <div className="bg-[#E2E8F0] flex justify-center items-center mr-[8px]">
          <span className="text-[#EF4444]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={13}
              height={12}
              fill="none"
              className="mr-2"
            >
              <path
                fill="#EF4444"
                d="M12.5 4.5a.5.5 0 0 1-.336.473L1 8.856V11.5a.5.5 0 1 1-1 0V.5A.5.5 0 0 1 .664.028l11.5 4a.5.5 0 0 1 .336.472Z"
              />
            </svg>
            Duplicate record found for this lead.
          </span>
          {canMergeOppor && (
            <span
              className="text-[#3B82F6] ml-1 cursor-pointer"
              onClick={() => {
                getDuplicateLeads(item, item?.potentialLeads);
                setSelectedLeadId(item?.id);
              }}
            >
              Merge It
            </span>
          )}
        </div>
      )}
      {item?.mergeHistory?.length > 0 && (
        <div className="bg-[#334155] flex justify-center items-center mr-[8px]">
          <span className="text-[#FFF]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={17}
              height={16}
              fill="none"
              className="mr-2"
            >
              <path
                fill="#fff"
                d="M16 6.5a.5.5 0 0 1-.336.473L4.5 10.856V13.5a.5.5 0 1 1-1 0v-11a.5.5 0 0 1 .664-.472l11.5 4A.5.5 0 0 1 16 6.5Z"
              />
            </svg>
            This is merged lead
          </span>
          <span
            className={`text-[#93C5FD] ml-1 ${
              isMergeLoading ? "opacity-60 cursor-default" : "cursor-pointer"
            }`}
            onClick={() => {
              // Keyed by stable lead id (not list index `i`) so appending merged
              // children / re-ordering / pagination can't shift a flag onto the
              // wrong lead. Absent id = collapsed → "Show All" by default.
              if (!isMergeLoading) handleToggleMerge();
            }}
          >
            {isMergeLoading ? "Loading..." : showAll?.[item?.id] ?? true ? "Show All" : "Close"}
          </span>
        </div>
      )}
    </>
  );
}

const TitleComponent = ({
  item,
  lang,
  setNoResponseNeededLoading,
  setLeadAsNoResponseNeeded,
  archivingLeadId,
  setArchivingLeadId,
  isLeadPinned,
  onTogglePin,
  isRowHovered,
  opportunityStatusList,
  selectedOppTypeId,
  selectedLead,
  user,
}) => {
  // Lead card normally reflects THIS lead's most-recent permitted
  // root opp — derived from ``item.leadOpportunities`` already in
  // the getLeads payload. Keeps the row stable on click.
  //
  // Exception: when this row IS the currently-selected lead AND the
  // user has explicitly switched the detail-panel tab, honor that
  // tab so the selected row stays in sync with the right panel.
  const isThisLeadSelected = +item?.id === +selectedLead?.id;
  const effectiveOppTypeId =
    isThisLeadSelected && selectedOppTypeId
      ? selectedOppTypeId
      : getMostRecentPermittedRootOppTypeId(item, user);
  const scopedOpps = effectiveOppTypeId
    ? (item?.leadOpportunities || []).filter(opp => oppMatchesTab(opp, effectiveOppTypeId))
    : item?.leadOpportunities || [];
  const latestOpportunity = orderBy(scopedOpps, opp => new Date(opp?.updatedDate), "desc")[0];
  let leadDate = item?.leadCreatedDate
    ? parseTimestamp(dayjs.utc(item?.leadCreatedDate).local())
    : parseTimestamp(new Date());

  const sortedMessages = (item?.messages || [])
    .filter(isConversationMessage)
    .map(el => ({ ...el, recentDate: el?.dateReceived || el?.dateSent }))
    .sort((a, b) => {
      const dateA = dayjs(a?.recentDate);
      const dateB = dayjs(b?.recentDate);
      return dateB.isBefore(dateA) ? -1 : 1;
    });

  if ((sortedMessages || []).length > 0) {
    const lastMsgLead = !isUndefined(sortedMessages[0]?.id) ? sortedMessages[0] : sortedMessages[1];
    leadDate = lastMsgLead?.dateReceived
      ? parseTimestamp(dayjs.utc(lastMsgLead?.dateReceived).local())
      : parseTimestamp(dayjs.utc(lastMsgLead?.dateSent).local());
  }

  return (
    <div className="flex flex-col">
      <div className="flex justify-between">
        <div className="flex-col-centered flex-1 min-w-0 items-start justify-start">
          <div className="flex items-center gap-2 w-full min-w-0">
            <Tooltip title={item?.fullName || ""} placement="topLeft">
              <span className="text-grey-700 ellipsis flex-shrink min-w-0">
                {item?.fullName || ""}
              </span>
            </Tooltip>
            {/* One small icon per root opp_type the lead actually has.
                The ``effectiveOppTypeId`` (this lead's most-recent
                permitted root, or the active tab when this row IS the
                selected lead) gets a tinted chip; the others render as
                bare strokes so the row signals "Sales AND Service
                opps exist, here's which one's active." */}
            <LeadOppTypeIcons lead={item} highlightedRootId={effectiveOppTypeId} />
          </div>
          <Tooltip
            title={getOpportunityStatusPath(
              opportunityStatusList,
              latestOpportunity?.opportunityStatus?.id
            )}
          >
            <span className={`text-xs ${statusColor(latestOpportunity?.opportunityStatus?.name)}`}>
              {latestOpportunity?.opportunityStatus?.name}
            </span>
          </Tooltip>
        </div>
        <div className="flex-col-centered shrink-0 items-end">
          <CustomLabel
            label={<div className="text-[12px] font-[400]">{` ${leadDate}`}</div>}
            labelClass="text-[#333] text-end"
          />{" "}
          <div className="flex-row-centered gap-3 whitespace-nowrap">
            <span
              onClick={e => {
                e.stopPropagation();
                if (archivingLeadId === item?.id) return;
                const isArchiveAction = item?.messageStatusType?.status !== "NO_RESPONSE_NEEDED";
                const archiveLead = () => {
                  setArchivingLeadId(item?.id);
                  setNoResponseNeededLoading(true);
                  setLeadAsNoResponseNeeded({
                    variables: {
                      leadId: item?.id,
                      isNoResponseNeeded: isArchiveAction,
                    },
                  });
                };

                if (getLocalStorage(SKIP_ARCHIVE_RESTORE_CONFIRMATION_KEY) === true) {
                  archiveLead();
                  return;
                }

                let dontAskAgain = false;
                Modal.confirm({
                  title: "Are you sure?",
                  icon: <ExclamationCircleFilled />,
                  content: (
                    <div>
                      <div
                        // eslint-disable-next-line react/no-danger
                        dangerouslySetInnerHTML={{
                          __html: `${`${
                            isArchiveAction
                              ? lang.noResponseNeededConfirmationMessage
                              : lang.restoreConfirmationMessage
                          }`.replace("__leadName__", item?.fullName)}`,
                        }}
                      />
                      <div className="mt-3 pt-3 border-0 border-t border-solid border-grey-200">
                        <Checkbox
                          onChange={ev => {
                            dontAskAgain = ev.target.checked;
                          }}
                        >
                          <span className="font-[500]">Don&apos;t ask me again</span>
                        </Checkbox>
                        <p className="text-xs text-grey-500 mt-1 mb-0 ml-6">
                          Future <strong>archive</strong> and <strong>restore</strong> actions will
                          be performed directly without this confirmation. You can re-enable the
                          confirmation anytime from your Personal Profile settings.
                        </p>
                      </div>
                    </div>
                  ),
                  onOk() {
                    if (dontAskAgain) {
                      setLocalStorage(SKIP_ARCHIVE_RESTORE_CONFIRMATION_KEY, true);
                    }
                    archiveLead();
                  },
                  onCancel() {},
                });
              }}
              className={`cursor-pointer flex-row-centered gap-1 items-center ${
                item?.messageStatusType?.status === "NO_RESPONSE_NEEDED" ||
                isRowHovered ||
                archivingLeadId === item?.id
                  ? "visible"
                  : "invisible"
              } ${archivingLeadId === item?.id ? "pointer-events-none opacity-80" : ""}`}
            >
              {archivingLeadId === item?.id ? (
                <LoadingOutlined className="text-brand-600" />
              ) : item?.messageStatusType?.status === "NO_RESPONSE_NEEDED" ? (
                <RestoreArchiveIcon />
              ) : (
                <ArchiveIcon />
              )}
              <span className="text-xs font-[400] text-brand-600">
                {archivingLeadId === item?.id
                  ? item?.messageStatusType?.status === "NO_RESPONSE_NEEDED"
                    ? "Restoring..."
                    : "Archiving..."
                  : item?.messageStatusType?.status === "NO_RESPONSE_NEEDED"
                  ? "Restore"
                  : "Archive"}
              </span>
            </span>
            <span
              onClick={e => {
                e.stopPropagation();
                onTogglePin();
              }}
              className={`cursor-pointer flex-row-centered gap-1 items-center ${
                isLeadPinned || isRowHovered ? "visible" : "invisible"
              }`}
            >
              {isLeadPinned ? <UnpinIcon /> : <PinIcon />}
              <span className="text-xs font-[400] text-brand-600">
                {isLeadPinned ? lang.unpin : lang.pin}
              </span>
            </span>
          </div>
        </div>
      </div>

      <div className="w-full my-2 h-[1px] bg-[#cbd5e1]" />
    </div>
  );
};
